import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { useGetCustomVariableValues } from '@/service-library/hooks/custom-variable-values'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import { createOrganizationMap } from '@/utils/organization'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import OrganizationTreeItem from '@/components/organizations-tree/OrganizationTreeItem'
import { OrgTreeSkeleton } from '@/components/organizations-tree/OrganizationTreeSearchSkeleton'
import { OrgTree } from '@/types/organizations'
import { useGetCustomVariables } from '@/service-library/hooks/custom-variables'
import { CustomVariableValue } from '@/types/custom-variables'

type VariableValue = {
  original: CustomVariableValue | undefined
  inherited: CustomVariableValue | undefined
}

type OrgTreeItemWithValueProps = {
  orgTree: OrgTree
  variableName: string | null
  orgValuesMap: Record<string, VariableValue>
} & React.ComponentProps<typeof OrganizationTreeItem>

function OrgTreeItemWithValue({
  orgTree,
  variableName,
  orgValuesMap,
  ...props
}: OrgTreeItemWithValueProps) {
  if (!variableName) {
    return <OrganizationTreeItem orgTree={orgTree} {...props} />
  }

  // Get the value for this specific organization
  const orgValue = orgValuesMap[orgTree.id]

  const label = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ pr: 2, justifyContent: 'space-between' }}
    >
      <Typography noWrap>{orgTree.name}</Typography>
      <Typography
        variant="body2"
        color={orgValue ? 'text.primary' : 'text.secondary'}
        sx={{
          fontStyle: orgValue.original ? 'normal' : 'italic',
          flexShrink: 0,
        }}
      >
        {/* Show the org value, then the inherited value */}
        {orgValue.original?.value || orgValue.inherited?.value || 'No value'}
      </Typography>
    </Stack>
  )

  return (
    <OrganizationTreeItem
      orgTree={orgTree}
      label={label}
      {...props}
      SubOrgComponent={OrgTreeItemWithValue}
      subOrgComponentProps={{
        variableName,
        orgValuesMap,
      }}
    />
  )
}

export default function ValuesAcrossOrgs() {
  const { rootOrganization } = useRootOrganization()
  const [selectedVariableId, setSelectedVariableId] = useState<string | null>(
    null,
  )
  const [expanded, setExpanded] = useState<string[]>([])

  // Get all organizations in the tree
  const { organizations, isFetchingAll } = useAllOrganizations({
    filters: {
      self_and_descendants_for_id: rootOrganization.id,
    },
  })

  // Get custom variables from the root organization
  const { customVariables = [] } = useGetCustomVariables({
    filters: {
      org_id: rootOrganization.id,
      limit: '1000',
    },
  })

  // Create the org tree for display
  const organizationMap = useMemo(
    () => (isFetchingAll ? {} : createOrganizationMap(organizations)),
    [isFetchingAll, organizations],
  )

  // When a variable is first selected, set up the expanded state
  useEffect(() => {
    if (selectedVariableId && !expanded.length) {
      setExpanded([rootOrganization.id])
    }
  }, [selectedVariableId, expanded.length, rootOrganization.id])

  const { customVariableValues, isLoading } = useGetCustomVariableValues({
    filters: {
      limit: '10000',
      custom_variable_id: selectedVariableId || undefined,
    },
    enabled: !!selectedVariableId,
  })

  const customVariableValuesMap = useMemo(() => {
    return customVariableValues?.reduce((acc, value) => {
      acc[value.owner_org_id] = value
      return acc
    }, {} as Record<string, CustomVariableValue>)
  }, [customVariableValues])

  const orgValuesMap = useMemo(() => {
    return organizations.reduce<Record<string, VariableValue>>((acc, org) => {
      const orgValue = customVariableValuesMap[org.id]
      acc[org.id] = {
        original: orgValue,
        inherited: undefined,
      }

      if (!orgValue) {
        // If no direct value, check parent orgs for inherited values
        let parentOrgId: string | null = org.parent_org_id
        while (
          parentOrgId &&
          !acc[org.id]?.original &&
          !acc[org.id]?.inherited
        ) {
          const parentValue = customVariableValuesMap[parentOrgId]
          if (parentValue) {
            acc[org.id] = {
              original: undefined,
              inherited: parentValue,
            }
          }
          const parentOrg = organizations.find((o) => o.id === parentOrgId)
          parentOrgId = parentOrg?.parent_org_id || null
        }
      }
      return acc
    }, {})
  }, [customVariableValuesMap, organizations])

  return (
    <Stack spacing={3} sx={{ mt: 2, pb: 4, width: '100%', maxWidth: 600 }}>
      <Card sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Values Across Organizations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a variable to see its value across all organizations in the
          tree. Values can be directly set or inherited from parent
          organizations.
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="variable-select-label">Select Variable</InputLabel>
          <Select
            labelId="variable-select-label"
            id="variable-select"
            value={selectedVariableId || ''}
            label="Select Variable"
            onChange={(e) => setSelectedVariableId(e.target.value)}
          >
            {customVariables.length > 0 ? (
              customVariables.map((variable) => (
                <MenuItem key={variable.id} value={variable.id}>
                  {variable.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                None
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Card>

      {selectedVariableId ? (
        <Box>
          {isFetchingAll || isLoading ? (
            <OrgTreeSkeleton />
          ) : (
            <SimpleTreeView
              expandedItems={expanded}
              onExpandedItemsChange={(_e, ids) => {
                setExpanded(ids)
              }}
            >
              <OrgTreeItemWithValue
                orgTree={organizationMap[rootOrganization.id]}
                variableName={
                  customVariables.find((v) => v.id === selectedVariableId)
                    ?.name || null
                }
                orgValuesMap={orgValuesMap}
              />
            </SimpleTreeView>
          )}
        </Box>
      ) : (
        <Alert severity="info" variant="outlined">
          Please select a variable to view its values across organizations.
        </Alert>
      )}
    </Stack>
  )
}
